import { useMemo } from 'react';
import type { PoseTimerState } from '@modelcast/types';
import { useRoom } from '../RoomProvider';
import { usePoseDisplay } from './usePoseDisplay';

const initialPoseState: PoseTimerState = {
  isActive: false,
  isPaused: false,
  isBreak: false,
  isUntimedBreak: false,
  isUntimed: false,
  currentPose: 0,
  totalPoses: 0,
  poseName: '',
  remaining: 0,
  elapsed: 0,
  duration: 0,
};

/**
 * Hook for accessing pose timer state from the central session state.
 * Uses usePoseDisplay for local countdown calculation from timestamps.
 */
export function usePoseTimer() {
  const { poseTimer } = useRoom();

  // Use central state, fall back to initial if not available
  const poseState = poseTimer ?? initialPoseState;

  // Use usePoseDisplay for local countdown calculation
  const display = usePoseDisplay(poseTimer);
  const displayPoseState = useMemo<PoseTimerState>(() => ({
    ...poseState,
    currentPose: display.currentIndex,
    totalPoses: display.totalPoses,
    poseName: display.poseName,
    remaining: display.remaining,
    elapsed: display.elapsed,
    duration: display.duration,
    isBreak: display.isBreak,
    isUntimed: poseState.isUntimed || display.duration === 0,
  }), [display, poseState]);

  // Computed values derived from central state
  const isInPose = useMemo(() =>
    displayPoseState.isActive && !displayPoseState.isBreak,
    [displayPoseState.isActive, displayPoseState.isBreak]
  );

  const isOnBreak = useMemo(() =>
    displayPoseState.isActive && displayPoseState.isBreak,
    [displayPoseState.isActive, displayPoseState.isBreak]
  );

  return {
    poseState,
    displayPoseState,
    remaining: display.remaining,
    elapsed: display.elapsed,
    isInPose,
    isOnBreak,
  };
}
